export type VoiceOptionPresentation = 'all' | 'examples' | 'none';

export interface VoicePresentation {
  spoken_prompt?: string;
  guide_instructions?: string;
  option_presentation?: VoiceOptionPresentation;
  examples?: string[];
  allow_other_answers?: boolean;
  spoken_content?: string;
}

export interface VoicePresentationOption {
  front_text: string;
  is_other?: boolean;
  is_active?: boolean;
}

const OWNED_FIELDS = [
  'spoken_prompt', 'guide_instructions', 'option_presentation', 'examples',
  'allow_other_answers', 'spoken_content',
] as const;
const CHOICE_TYPES = new Set(['multiple_choice', 'multiple_select']);
const VISUAL_TYPES = new Set(['image', 'video_player', 'pdf', 'radar_chart']);

const record = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : {}
);

export const readVoicePresentation = (metadata?: Record<string, unknown>): VoicePresentation => {
  const voice = record(metadata?.voice);
  return {
    spoken_prompt: typeof voice.spoken_prompt === 'string' ? voice.spoken_prompt : '',
    guide_instructions: typeof voice.guide_instructions === 'string' ? voice.guide_instructions : '',
    option_presentation: voice.option_presentation === 'examples' || voice.option_presentation === 'none'
      ? voice.option_presentation : 'all',
    examples: Array.isArray(voice.examples)
      ? voice.examples.filter((item): item is string => typeof item === 'string') : [],
    allow_other_answers: voice.allow_other_answers === true,
    spoken_content: typeof voice.spoken_content === 'string' ? voice.spoken_content : '',
  };
};

export const mergeVoicePresentation = (
  metadata: Record<string, unknown> | undefined,
  patch: Partial<VoicePresentation>,
): Record<string, unknown> => {
  const voice = { ...record(metadata?.voice) };
  for (const field of OWNED_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(patch, field)) continue;
    const value = patch[field];
    const empty = value === undefined || value === false
      || (typeof value === 'string' && !value.trim())
      || (Array.isArray(value) && !value.length)
      || (field === 'option_presentation' && value === 'all');
    if (empty) delete voice[field];
    else voice[field] = value;
  }
  const next = { ...metadata };
  if (Object.keys(voice).length) next.voice = voice;
  else delete next.voice;
  return next;
};

export const resetVoicePresentation = (metadata?: Record<string, unknown>) => (
  mergeVoicePresentation(metadata, Object.fromEntries(OWNED_FIELDS.map((field) => [field, undefined])))
);

export const validateVoicePresentation = (
  metadata: Record<string, unknown> | undefined,
  questionType: string,
  options: readonly VoicePresentationOption[],
): string | null => {
  if (metadata?.voice === undefined) return null;
  if (!metadata.voice || typeof metadata.voice !== 'object' || Array.isArray(metadata.voice)) {
    return 'Voice presentation must be a JSON object.';
  }
  const voice = record(metadata.voice);
  for (const key of ['spoken_prompt', 'guide_instructions', 'spoken_content']) {
    if (voice[key] !== undefined && typeof voice[key] !== 'string') {
      return `Voice ${key.replace(/_/g, ' ')} must be text.`;
    }
  }
  if (voice.option_presentation !== undefined
      && !['all', 'examples', 'none'].includes(String(voice.option_presentation))) {
    return 'Voice option presentation must be all, examples, or none.';
  }
  if (voice.examples !== undefined && (!Array.isArray(voice.examples)
      || voice.examples.some((item) => typeof item !== 'string' || !item.trim()))) {
    return 'Voice examples must be a list of nonempty text entries.';
  }
  if (voice.allow_other_answers !== undefined && typeof voice.allow_other_answers !== 'boolean') {
    return 'Voice allow other answers must be true or false.';
  }
  if (voice.allow_other_answers === true && (!CHOICE_TYPES.has(questionType)
      || !options.some((option) => option.is_other && option.is_active !== false))) {
    return 'Allowing other answers requires an active Other option on a multiple choice or multiple select question.';
  }
  return null;
};

export const previewVoicePresentation = (
  metadata: Record<string, unknown> | undefined,
  questionType: string,
  text: string,
  options: readonly VoicePresentationOption[],
) => {
  const voice = readVoicePresentation(metadata);
  const activeOptions = options.filter((option) => option.is_active !== false);
  const labels = activeOptions.map((option) => option.front_text.trim()).filter(Boolean);
  const prompt = voice.spoken_prompt?.trim() || text;
  const examples = voice.examples?.length ? voice.examples : labels.slice(0, 3);
  const choices = voice.option_presentation === 'none' ? []
    : voice.option_presentation === 'examples' ? examples : labels;
  return {
    prompt,
    choices,
    canReadAll: labels.length > 0,
    skipped: VISUAL_TYPES.has(questionType) && !voice.spoken_content?.trim(),
    spokenContent: voice.spoken_content?.trim() || '',
  };
};
